import React, { useState, useEffect, useCallback, useMemo } from 'react'
import { Spin, message, Card } from 'antd'
import axios from 'util/Api'
import Board from './Board'
import FilterBar from './FilterBar'
import StatusFilters from './StatusFilters'
import DetailsDrawer from './DetailsDrawer'
import { parseDateOnly, isExpectedCompletionPast, isReviewDatePast } from './utils'
import './styles.css'

function getBaseUrl() {
    const hostname = window.location.hostname
    const protocol = window.location.protocol

    // Check if running on localhost
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
        return `${protocol}//localhost:3000`
    }

    // For production/staging, return subdomain.domain format
    return `${protocol}//${hostname}`
}

function JobListCardView(props) {
    const [jobCards, setJobCards] = useState([])
    const [loading, setLoading] = useState(true)
    const [searchTerm, setSearchTerm] = useState('')
    const [activeFilterTypes, setActiveFilterTypes] = useState(new Set(['presentStatus']))
    const [enabledFilterValues, setEnabledFilterValues] = useState(new Map())
    const [expandedColumnId, setExpandedColumnId] = useState(null)
    const [showUnserviceableOnly, setShowUnserviceableOnly] = useState(false)
    const [showExpiredOnly, setShowExpiredOnly] = useState(false)
    const [showClosedPIOnly, setShowClosedPIOnly] = useState(false)
    const [selectedCard, setSelectedCard] = useState(null)
    const [detailsVisible, setDetailsVisible] = useState(false)

    // Fetch job cards from API
    useEffect(() => {
        loadData()
    }, [])

    const loadData = async () => {
        try {
            // const organisation = props.authUser.organisation;
            // console.log(organisation);
            setLoading(true)
            const response = await axios.get('/job-card/all-optimized?status=1')

            // Handle different response formats
            let data = []
            if (response && response.data) {
                if (Array.isArray(response.data)) {
                    data = response.data
                } else if (response.data && Array.isArray(response.data.data)) {
                    data = response.data.data
                } else if (response.data && Array.isArray(response.data.resources)) {
                    data = response.data.resources
                } else {
                    data = []
                }
            }

            // Filter only open job cards
            const openCards = data.filter(
                (c) => (c.status || '').toLowerCase() === 'open'
            )

            // Map API response to card format (adjust field names as needed)
            const mappedCards = openCards.map((card) => ({
                jobCardId: card._id || card.id || '',
                jobCardNo: card.jobCardNo || card.job_card_no || card['Job Card No'] || '',
                assetCode: card.assetCode || card.asset_code || card['Asset Code'] || '',
                ownerCode: card.ownerCode || card.owner_code || card['Owner Code'] || '',
                assetSubType: card.assetSubType || card.asset_sub_type || card['Asset Sub Type'] || '',
                location: card.location || card['Location'] || '',
                status: card.status || card['Status'] || '',
                expectedCompletion: card.expectedCompletion || card.expected_completion || card['Expected Date Of Completion'] || '',
                presentStatus: card.presentStatus || card.present_status || card['Present Status'] || '',
                presentStatusColor: card.presentStatusColor || card.present_status_color || card['Present Status Color'] || '',
                presentStatusRemark: card.presentStatusRemark || card.present_status_remark || card['Present Status Remark'] || '',
                detailsOfComplaint: card.detailsOfComplaint || card.details_of_complaint || card['Details Of Complaint'] || '',
                causeOfDamage: card.causeOfDamage || card.cause_of_damage || card['Cause Of Damage'] || '',
                subLocation: card.subLocation || card.sub_location || card['Sub Location'] || '',
                complaintDate: card.complaintDate || card.complaint_date || card['Complaint Date'] || '',
                owner: card.owner || card['Owner'] || '',
                assetType: card.assetType || card.asset_type || card['Asset Type'] || '',
                complaintNo: card.complaintNo || card.complaint_no || card['Complaint'] || '',
                remark: card.remark || card['Remark'] || '',
                cancelDate: card.cancelDate || card.cancel_date || card['Cancel Date'] || '',
                uncancelDate: card.uncancelDate || card.uncancel_date || card['Uncancel Date'] || '',
                closureDate: card.closureDate || card.closure_date || card['Closure Date'] || '',
                followUp: card.followUp || card.follow_up || card['Follow Up'] || '',
                followUpDate: card.followUpDate || card.follow_up_date || card['Follow Up Date'] || '',
                reviewDate: card.reviewDate || card.review_date || card['Review Date'] || '',
                createdAt: card.createdAt || card.created_at || card['Created At'] || '',
                updatedAt: card.updatedAt || card.updated_at || card['Updated At'] || '',
                totalAmount: card.totalAmount || card.total_amount || card['Total Amount'] || '',
                pendingInvoice: card.pendingInvoice || card.pending_invoice || card['Pending Invoice'] || '',
                assetUnservicable: card.assetUnservicable || card.asset_unservicable || card['Asset Unservicable'] || '',
                statusTitle: card.statusTitle || card.status_title || card['Status Title'] || '',
                jobCardUrl: getBaseUrl() + '/job-card/report/' + (card._id || card.jobCardId || '')
            }))

            setJobCards(mappedCards)

            // Initialize filter values for active filter types
            initializeFilterValues(mappedCards)
        } catch (error) {
            console.error('Error loading job cards:', error)
            message.error('Failed to load job cards. Please try again.')
        } finally {
            setLoading(false)
        }
    }

    const initializeFilterValues = (cards) => {
        const newEnabledValues = new Map()

        activeFilterTypes.forEach((filterType) => {
            const allValues = new Set()
            cards.forEach((c) => {
                const value = getFilterValue(c, filterType)
                if (value) {
                    allValues.add(value)
                }
            })

            // Enable all values by default
            newEnabledValues.set(filterType, new Set(allValues))
        })

        setEnabledFilterValues(newEnabledValues)
    }

    const getFilterFieldName = (filterType) => {
        const fieldMap = {
            'presentStatus': 'presentStatus',
            'location': 'location',
            'assetType': 'assetType',
            'assetSubType': 'assetSubType',
            'subLocation': 'subLocation',
            'organisation': 'organisationName'
        }
        return fieldMap[filterType] || null
    }

    const getFilterValue = (card, filterType) => {
        const fieldName = getFilterFieldName(filterType)
        if (!fieldName) return ''
        return (card[fieldName] || '').trim()
    }

    const getFilterTypeLabel = (filterType) => {
        const labelMap = {
            'presentStatus': 'Present Status',
            'location': 'Location',
            'assetType': 'Asset Type',
            'assetSubType': 'Asset Sub Type',
            'subLocation': 'Sub Location',
            'organisation': 'Organisation'
        }
        return labelMap[filterType] || filterType
    }

    const matchesFilters = useCallback((card) => {
        // Check all active dynamic filters
        for (const filterType of activeFilterTypes) {
            const cardValue = getFilterValue(card, filterType)

            if (!enabledFilterValues.has(filterType)) {
                continue
            }

            const enabledValues = enabledFilterValues.get(filterType)

            const allValues = new Set()
            jobCards.forEach((c) => {
                const val = getFilterValue(c, filterType)
                if (val) allValues.add(val)
            })

            // If no values are enabled (Select None was clicked), filter out all cards
            if (enabledValues.size === 0) {
                return false
            }

            // If some values are selected (but not all), filter by those values
            if (enabledValues.size > 0 && enabledValues.size < allValues.size) {
                if (!enabledValues.has(cardValue)) {
                    return false
                }
            }
            // If all values are selected, show all cards (no filtering needed)
        }

        if (searchTerm) {
            const haystack = (
                card.jobCardNo +
                ' ' +
                card.detailsOfComplaint +
                ' ' +
                card.assetType +
                ' ' +
                card.assetSubType +
                ' ' +
                card.assetCode +
                ' ' +
                card.location +
                ' ' +
                card.subLocation
            ).toLowerCase()
            if (!haystack.includes(searchTerm.toLowerCase())) {
                return false
            }
        }

        if (showUnserviceableOnly) {
            const isUnserviceable = (card.assetUnservicable || '').toString().toLowerCase().trim() === 'true'
            if (!isUnserviceable) {
                return false
            }
        }

        if (showExpiredOnly) {
            if (!isReviewDatePast(card)) {
                return false
            }
        }

        if (showClosedPIOnly) {
            if ((card.statusTitle || '').trim() !== 'ClosedPendingInvoice') {
                return false
            }
        }

        return true
    }, [activeFilterTypes, enabledFilterValues, jobCards, searchTerm, showUnserviceableOnly, showExpiredOnly, showClosedPIOnly])

    const filteredCards = useMemo(() => {
        return jobCards.filter(matchesFilters)
    }, [jobCards, matchesFilters])

    const handleFilterTypeToggle = (filterType, isChecked) => {
        const newActiveTypes = new Set(activeFilterTypes)
        const newEnabledValues = new Map(enabledFilterValues)

        if (isChecked) {
            newActiveTypes.add(filterType)

            // Initialize enabled values for this filter type
            if (!newEnabledValues.has(filterType)) {
                const allValues = new Set()
                jobCards.forEach((c) => {
                    const val = getFilterValue(c, filterType)
                    if (val) allValues.add(val)
                })
                newEnabledValues.set(filterType, new Set(allValues))
            }
        } else {
            newActiveTypes.delete(filterType)
            newEnabledValues.delete(filterType)
        }

        setActiveFilterTypes(newActiveTypes)
        setEnabledFilterValues(newEnabledValues)
    }

    const handleToggleFilterValue = (filterType, value) => {
        const newEnabledValues = new Map(enabledFilterValues)

        if (!newEnabledValues.has(filterType)) {
            newEnabledValues.set(filterType, new Set())
        }

        const enabledValues = newEnabledValues.get(filterType)

        if (enabledValues.has(value)) {
            enabledValues.delete(value)
        } else {
            enabledValues.add(value)
        }

        // If all are disabled, enable all (prevent empty state)
        const allValues = new Set()
        jobCards.forEach((c) => {
            const val = getFilterValue(c, filterType)
            if (val) allValues.add(val)
        })

        if (enabledValues.size === 0) {
            allValues.forEach((v) => enabledValues.add(v))
        }

        setEnabledFilterValues(newEnabledValues)
    }

    const handleSelectAll = (filterType) => {
        const newEnabledValues = new Map(enabledFilterValues)

        if (!newEnabledValues.has(filterType)) {
            newEnabledValues.set(filterType, new Set())
        }

        const enabledValues = newEnabledValues.get(filterType)
        const allValues = new Set()
        jobCards.forEach((c) => {
            const val = getFilterValue(c, filterType)
            if (val) allValues.add(val)
        })

        // Add all values (like clicking each tag individually)
        allValues.forEach((v) => enabledValues.add(v))

        setEnabledFilterValues(newEnabledValues)
    }

    const handleSelectNone = (filterType) => {
        const newEnabledValues = new Map(enabledFilterValues)

        // Create a new empty Set to deselect all values
        // This is the opposite of Select All
        newEnabledValues.set(filterType, new Set())

        // Update state to trigger UI refresh and filtering
        setEnabledFilterValues(newEnabledValues)
    }

    const handleReset = () => {
        setSearchTerm('')
        setShowUnserviceableOnly(false)
        setShowExpiredOnly(false)
        setShowClosedPIOnly(false)

        // Reset dynamic filters - enable all values for all active filter types
        const newEnabledValues = new Map()
        activeFilterTypes.forEach((filterType) => {
            const allValues = new Set()
            jobCards.forEach((c) => {
                const val = getFilterValue(c, filterType)
                if (val) allValues.add(val)
            })
            newEnabledValues.set(filterType, new Set(allValues))
        })

        setEnabledFilterValues(newEnabledValues)
    }

    const handleDownloadCsv = () => {
        if (filteredCards.length === 0) {
            message.warning('No cards to export. Please adjust your filters.')
            return
        }

        const fieldNames = [
            'Job Card No',
            'Asset Code',
            'Owner Code',
            'Asset Sub Type',
            'Location',
            'Status',
            'Expected Date Of Completion',
            'Present Status',
            'Present Status Color',
            'Present Status Remark',
            'Details Of Complaint',
            'Cause Of Damage',
            'Sub Location',
            'Complaint Date',
            'Owner',
            'Asset Type',
            'Complaint',
            'Remark',
            'Cancel Date',
            'Uncancel Date',
            'Closure Date',
            'Follow Up',
            'Follow Up Date',
            'Review Date',
            'Created At',
            'Updated At',
            'Total Amount',
            'Pending Invoice',
            'Asset Unservicable',
            'Job Card URL'
        ]

        const cardToRow = (card) => {
            return fieldNames.map(fieldName => {
                const fieldMap = {
                    'Job Card No': card.jobCardNo || '',
                    'Asset Code': card.assetCode || '',
                    'Owner Code': card.ownerCode || '',
                    'Asset Sub Type': card.assetSubType || '',
                    'Location': card.location || '',
                    'Status': card.status || '',
                    'Expected Date Of Completion': card.expectedCompletion || '',
                    'Present Status': card.presentStatus || '',
                    'Present Status Color': card.presentStatusColor || '',
                    'Present Status Remark': card.presentStatusRemark || '',
                    'Details Of Complaint': card.detailsOfComplaint || '',
                    'Cause Of Damage': card.causeOfDamage || '',
                    'Sub Location': card.subLocation || '',
                    'Complaint Date': card.complaintDate || '',
                    'Owner': card.owner || '',
                    'Asset Type': card.assetType || '',
                    'Complaint': card.complaintNo || '',
                    'Remark': card.remark || '',
                    'Cancel Date': card.cancelDate || '',
                    'Uncancel Date': card.uncancelDate || '',
                    'Closure Date': card.closureDate || '',
                    'Follow Up': card.followUp || '',
                    'Follow Up Date': card.followUpDate || '',
                    'Review Date': card.reviewDate || '',
                    'Created At': card.createdAt || '',
                    'Updated At': card.updatedAt || '',
                    'Total Amount': card.totalAmount || '',
                    'Pending Invoice': card.pendingInvoice || '',
                    'Asset Unservicable': card.assetUnservicable || '',
                    'Job Card URL': card.jobCardUrl || ''
                }

                const value = fieldMap[fieldName] || ''
                return value.toString().replace(/;/g, ';;').replace(/\n/g, ' ')
            })
        }

        const csvRows = [
            fieldNames.join(';'),
            ...filteredCards.map(card => cardToRow(card).join(';'))
        ]

        const csvContent = csvRows.join('\n')

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
        const link = document.createElement('a')
        const url = URL.createObjectURL(blob)
        link.setAttribute('href', url)
        link.setAttribute('download', `job_cards_${new Date().toISOString().split('T')[0]}.csv`)
        link.style.visibility = 'hidden'
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
    }

    const handleCardClick = (card) => {
        setSelectedCard(card)
        setDetailsVisible(true)
    }

    const handleToggleExpand = (stageId) => {
        setExpandedColumnId(expandedColumnId === stageId ? null : stageId)
    }

    if (loading) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                <Spin size="large" tip="Loading job cards..." />
            </div>
        )
    }

    return (
        <Card className="gx-card-metrics"
            style={{ backgroundColor: 'transparent', padding: '0', margin: '0' }}
            bodyStyle={{ padding: '0', margin: '0' }}>
            <FilterBar
                searchTerm={searchTerm}
                onSearchChange={setSearchTerm}
                activeFilterTypes={activeFilterTypes}
                onFilterTypeToggle={handleFilterTypeToggle}
                showUnserviceableOnly={showUnserviceableOnly}
                onUnserviceableToggle={setShowUnserviceableOnly}
                showExpiredOnly={showExpiredOnly}
                onExpiredToggle={setShowExpiredOnly}
                showClosedPIOnly={showClosedPIOnly}
                onClosedPIToggle={setShowClosedPIOnly}
                onReset={handleReset}
                onDownloadCsv={handleDownloadCsv}
                onRefresh={loadData}
                loading={loading}
            />

            <StatusFilters
                activeFilterTypes={activeFilterTypes}
                enabledFilterValues={enabledFilterValues}
                jobCards={jobCards}
                searchTerm={searchTerm}
                showUnserviceableOnly={showUnserviceableOnly}
                showExpiredOnly={showExpiredOnly}
                showClosedPIOnly={showClosedPIOnly}
                onToggleFilterValue={handleToggleFilterValue}
                onSelectAll={handleSelectAll}
                onSelectNone={handleSelectNone}
                getFilterFieldName={getFilterFieldName}
                getFilterValue={getFilterValue}
                getFilterTypeLabel={getFilterTypeLabel}
            />

            <Board
                jobCards={filteredCards}
                expandedColumnId={expandedColumnId}
                onCardClick={handleCardClick}
                onToggleExpand={handleToggleExpand}
            />

            <DetailsDrawer
                card={selectedCard}
                visible={detailsVisible}
                onClose={() => {
                    setDetailsVisible(false)
                    setSelectedCard(null)
                }}
            />
        </Card>
    )
}

export default JobListCardView
